import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Users, CalendarDays, MessageCircle, Target, Loader2 } from "lucide-react";

const STATUS_LABELS = { new: "Yeni", contacted: "İletişime Geçildi", converted: "Dönüştü", lost: "Kaybedildi" };
const STATUS_COLORS = { new: "bg-blue-500/15 text-blue-400", contacted: "bg-yellow-500/15 text-yellow-400", converted: "bg-emerald-500/15 text-emerald-400", lost: "bg-red-500/15 text-red-400" };

export default function AdminDashboard() {
  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ["admin-summary"],
    queryFn: async () => (await api.get("/admin/analytics/summary")).data,
  });

  const { data: leads, isLoading: loadingLeads } = useQuery({
    queryKey: ["admin-recent-leads"],
    queryFn: async () => (await api.get("/admin/leads")).data,
  });

  const recentLeads = (leads || []).slice(0, 5);

  const cards = [
    { label: "Toplam Lead", value: summary?.total_leads ?? 0, icon: Users },
    { label: "Bugünkü Lead", value: summary?.today_leads ?? 0, icon: CalendarDays },
    { label: "WhatsApp Tıklama", value: summary?.whatsapp_clicks ?? 0, icon: MessageCircle },
    { label: "Dönüşen Lead", value: summary?.status_summary?.converted ?? 0, icon: Target },
  ];

  return (
    <div data-testid="admin-dashboard-page">
      <h1 className="text-xl font-medium text-white">Genel Bakış</h1>
      <p className="mt-1 text-sm text-slate-400">Reklam performansı ve lead durumunuza genel bir bakış.</p>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {cards.map(({ label, value, icon: Icon }) => (
          <Card key={label} className="border-white/10 bg-[#0A0A0A]" data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="flex items-center justify-between p-5">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
                <p className="mt-1 text-2xl font-bold text-white">
                  {loadingSummary ? <Loader2 className="h-5 w-5 animate-spin text-gold" /> : value}
                </p>
              </div>
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-gold/20 bg-gold/[0.06]">
                <Icon className="h-5 w-5 text-gold-light" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-6 border-white/10 bg-[#0A0A0A]">
        <CardHeader>
          <CardTitle className="text-base text-white">Son Leadler</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingLeads ? (
            <Loader2 className="h-5 w-5 animate-spin text-gold" />
          ) : recentLeads.length === 0 ? (
            <p className="text-sm text-slate-400" data-testid="dashboard-no-leads">Henüz lead bulunmuyor.</p>
          ) : (
            <div className="space-y-2">
              {recentLeads.map((lead) => (
                <Link
                  key={lead._id}
                  to={`/admin/leads/${lead._id}`}
                  className="flex items-center justify-between rounded-lg border border-white/5 p-3 transition-colors hover:bg-white/5"
                  data-testid={`dashboard-lead-row-${lead._id}`}
                >
                  <div>
                    <p className="text-sm font-medium text-white">{lead.name}</p>
                    <p className="text-xs text-slate-500">{lead.phone}</p>
                  </div>
                  <Badge className={`${STATUS_COLORS[lead.status]} border-none`}>{STATUS_LABELS[lead.status]}</Badge>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
